/**
 * Applies a partner's brand_config to the DOM (called from
 * `PartnerScopeProvider`).
 *
 * Writes CSS custom properties under the `--brand-*` namespace via a
 * single managed `<style id="tilt-brand-overrides">` element in
 * `<head>`. Index.css references those vars as fallbacks inside
 * `var(--brand-primary, default)` expressions (both `:root` and
 * `.dark`) so the token cascade is:
 *
 *   1. User preference override   (inline style on `<html>`: `--primary`)
 *   2. Partner brand_config         (rules in the managed style element)
 *   3. shadcn/ui default           (hardcoded fallback in index.css,
 *                                    different values per light/dark)
 *
 * Preferences always win over brand, because the user chose them.
 *
 * Why a `<style>` element instead of inline `html.style.setProperty`:
 *   per-theme brand variants (`brand_config.dark.X`) need to apply
 *   *only* when `<html class="dark">`. Inline styles can't be scoped
 *   to a class, but a `<style>` element can carry a `:root { ... }`
 *   block and a `html.dark { ... }` block side-by-side. CSS specificity
 *   then handles theme switching automatically - no JS subscription
 *   to the theme picker required.
 *
 * Brand config values are HSL triples (e.g. "221 83% 53%") - shadcn's
 * convention so tokens can be composed with `hsl(var(--primary) / 0.5)`.
 * Invalid formats are silently dropped to avoid blank screens on bad
 * config. Call this with `null` to clear a previously-applied brand.
 *
 * Beyond colors, this also injects:
 *   - body / heading / mono font families (with per-theme variants)
 *   - external font @import via a managed <link> element
 *
 * The full white-label surface is documented on `BrandConfig` in
 * the SDK.
 */

import type { BrandConfig } from "@tilt/admin-sdk";

/** Color CSS property name keyed off a brand_config field. */
const BRAND_COLOR_VARS: Array<{ key: string; var: string }> = [
  { key: "primary_color", var: "--brand-primary" },
  { key: "primary_foreground", var: "--brand-primary-foreground" },
  { key: "background", var: "--brand-background" },
  { key: "foreground", var: "--brand-foreground" },
  { key: "accent", var: "--brand-accent" },
];

// Dark-mode variants write to separate --brand-*-dark custom properties so
// the dark CSS fallback chain is independent of the light-mode brand values.
// Without this, a brand's light primary (potentially too dark for dark bg)
// bleeds into dark mode via the :root cascade, failing WCAG contrast.
const DARK_BRAND_COLOR_VARS: Array<{ key: string; var: string }> = [
  { key: "primary_color", var: "--brand-primary-dark" },
  { key: "primary_foreground", var: "--brand-primary-foreground-dark" },
  { key: "background", var: "--brand-background-dark" },
  { key: "foreground", var: "--brand-foreground-dark" },
  { key: "accent", var: "--brand-accent-dark" },
];

/** Font CSS property name keyed off a brand_config field. */
const BRAND_FONT_VARS: Array<{ key: string; var: string }> = [
  { key: "font_family", var: "--brand-font-family" },
  { key: "heading_font_family", var: "--brand-heading-font-family" },
  { key: "mono_font_family", var: "--brand-mono-font-family" },
];

/** Valid HSL triple shaped like "221 83% 53%" or "221.2 83.2% 53.3%". */
const HSL_TRIPLE_RE = /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/;

const STYLE_ELEMENT_ID = "tilt-brand-overrides";
const FONT_LINK_ELEMENT_ID = "tilt-brand-font-import";

function buildRuleBlock(
  selector: string,
  config: Record<string, unknown>,
  colorVars: typeof BRAND_COLOR_VARS = BRAND_COLOR_VARS,
): string {
  const lines: string[] = [];
  for (const { key, var: name } of colorVars) {
    const value = config[key];
    if (typeof value === "string" && HSL_TRIPLE_RE.test(value.trim())) {
      lines.push(`  ${name}: ${value.trim()};`);
    }
  }
  for (const { key, var: name } of BRAND_FONT_VARS) {
    const value = config[key];
    if (typeof value === "string" && value.trim().length > 0) {
      // Font-family values are quoted as-is; partners write standard
      // CSS font-family lists (e.g. ``"Inter", sans-serif``).
      lines.push(`  ${name}: ${value.trim()};`);
    }
  }
  if (lines.length === 0) return "";
  return `${selector} {\n${lines.join("\n")}\n}\n`;
}

/** Manage a <link rel=stylesheet> for the partner's font_import_url.
 *  Null/blank removes any previously-injected link. */
function applyFontImport(url: string | null | undefined): void {
  let linkEl = document.getElementById(FONT_LINK_ELEMENT_ID) as HTMLLinkElement | null;
  const trimmed = typeof url === "string" ? url.trim() : "";
  if (!trimmed) {
    if (linkEl) linkEl.remove();
    return;
  }
  // Only allow https URLs - blocks `javascript:` / `data:` payloads
  // that would let a malicious brand_config inject script content.
  if (!/^https:\/\//i.test(trimmed)) {
    if (linkEl) linkEl.remove();
    return;
  }
  if (!linkEl) {
    linkEl = document.createElement("link");
    linkEl.id = FONT_LINK_ELEMENT_ID;
    linkEl.rel = "stylesheet";
    document.head.appendChild(linkEl);
  }
  if (linkEl.href !== trimmed) {
    linkEl.href = trimmed;
  }
}

export function applyBrand(config: BrandConfig | null | undefined): void {
  if (typeof document === "undefined") return;

  let styleEl = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = STYLE_ELEMENT_ID;
    document.head.appendChild(styleEl);
  }

  if (!config) {
    styleEl.textContent = "";
    applyFontImport(null);
    return;
  }

  const lightBlock = buildRuleBlock(":root", config as Record<string, unknown>);
  const darkBlock = buildRuleBlock(
    "html.dark",
    (config.dark ?? {}) as Record<string, unknown>,
    DARK_BRAND_COLOR_VARS,
  );
  styleEl.textContent = lightBlock + darkBlock;
  applyFontImport(typeof config.font_import_url === "string" ? config.font_import_url : null);
}

/**
 * Resolves what to render in chrome ("topbar + login + sidebar") when
 * brand config omits one of the display fields. Keeps fallbacks in one
 * place so the portal renders a coherent default (Finterra) everywhere.
 */
export function brandDisplay(config: BrandConfig | null | undefined): {
  logoUrl: string;
  productName: string;
  tagline: string | null;
} {
  const logo =
    typeof config?.logo_url === "string" && config.logo_url.trim().length > 0
      ? config.logo_url
      : "/finterra-logo.jpg";
  const name =
    typeof config?.product_name === "string" && config.product_name.trim().length > 0
      ? config.product_name
      : "Finterra";
  const tagline =
    typeof config?.tagline === "string" && config.tagline.trim().length > 0
      ? config.tagline.trim()
      : null;
  return { logoUrl: logo, productName: name, tagline };
}
